import {
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  tableCellClasses,
  Grid,
  TextField,
  Autocomplete,
  InputAdornment,
  Fade,
  Fab
} from '@mui/material';
import { FC, useEffect, useState, useRef } from 'react';
import { useAppSelector } from "../../store/hooks";
import styled_ from "@emotion/styled";
import moment from 'moment';
import { Utils } from "../../utils/common-utils";
import ReusableReportHeader from './ReusableReportHeader';
import axios from '../../utils/axios';
import { toast } from 'react-toastify';
import Filters from '../Filters/Filters';
import { Search as SearchIcon, ArrowUpward as ArrowUpIcon, ArrowDownward as ArrowDownIcon } from '@mui/icons-material';

const StyledTableCell = styled_(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: '#eaecf0',
    color: '#000',
    border: '0.75px solid #d9d4d4',
    margin: 0,
    padding: 5,
    fontSize: '9pt',
    paddingLeft: '15px !important',
    fontWeight: 600,
  },
  [`&.${tableCellClasses.body}`]: {
    verticalAlign: 'super',
    fontSize: '9pt',
    fontWeight: 'normal',
    color: '#292d35',
    padding: 5,
    border: '0.75px solid #d9d4d4',
    paddingLeft: '15px !important',
    margin: 0,
    backgroundColor: '#fff',
  },
}));

const StyledTableRow = styled_(TableRow)(({ theme }) => ({}));

interface Employee {
  _id: string;
  name: string;
  empId: string;
  designation?: string;
  department?: string;
}

interface LedgerEntry {
  employeeId: string;
  employeeName: string;
  employeeCode: string;
  designation?: string;
  department?: string;
  date: string;
  voucherNumber: string;
  ledgerName: string;
  ledgerCode: string;
  primaryGroup: string;
  groupCode: string;
  particular: string;
  narration: string;
  debit: number;
  credit: number;
  balance: number;
  journalEntryId: string;
  openingBalance: number;
}

const EmployeeLedger: FC = () => {
  const { selectedAccount } = useAppSelector(state => state.auth);

  const [employees, setEmployees] = useState<Employee[]>([]);
  const [selectedEmployees, setSelectedEmployees] = useState<Employee[]>([]);
  const [filters, setFilters] = useState('');
  const [filterData, setFilterData] = useState<any>();
  const [isLoading, setIsLoading] = useState(false);
  const [allLedgerEntries, setAllLedgerEntries] = useState<LedgerEntry[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredEntries, setFilteredEntries] = useState<LedgerEntry[]>([]);
  const [summary, setSummary] = useState<any>(null);

  // Scroll button states
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [scrollDirection, setScrollDirection] = useState<'up' | 'down'>('down');
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastScrollTop = useRef(0);

  // Fetch report data when filters change
  useEffect(() => {
    if (filters && selectedAccount?._id) {
      fetchReport();
    } else {
      setAllLedgerEntries([]);
      setFilteredEntries([]);
      setSummary(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, selectedAccount]);

  // Filter entries based on selected employees and search term (client-side filtering)
  useEffect(() => {
    if (!allLedgerEntries || allLedgerEntries.length === 0) {
      setFilteredEntries([]);
      return;
    }

    let filtered = [...allLedgerEntries];

    // Filter by selected employees
    if (selectedEmployees.length > 0) {
      const selectedIds = selectedEmployees.map(emp => emp._id);
      filtered = filtered.filter((entry: LedgerEntry) =>
        selectedIds.includes(entry.employeeId)
      );
    }

    // Filter by search term
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter((entry: LedgerEntry) =>
        entry.voucherNumber?.toLowerCase().includes(searchLower) ||
        entry.ledgerName?.toLowerCase().includes(searchLower) ||
        entry.narration?.toLowerCase().includes(searchLower) ||
        entry.particular?.toLowerCase().includes(searchLower) ||
        entry.employeeName?.toLowerCase().includes(searchLower) ||
        entry.employeeCode?.toLowerCase().includes(searchLower)
      );
    }

    setFilteredEntries(filtered);

    // Recalculate summary for filtered entries
    if (filtered.length > 0) {
      const newSummary = {
        totalDebits: filtered.reduce((sum, entry) => sum + (entry.debit || 0), 0),
        totalCredits: filtered.reduce((sum, entry) => sum + (entry.credit || 0), 0),
        totalEntries: filtered.length
      };
      setSummary(newSummary);
    } else {
      setSummary(null);
    }
  }, [searchTerm, selectedEmployees, allLedgerEntries]);

  const fetchReport = async () => {
    if (!filters || !selectedAccount?._id) return;

    setIsLoading(true);

    // Parse filters to extract dates
    const urlParams = new URLSearchParams(filters);
    const startDate = urlParams.get('startDate') || urlParams.get('start');
    const endDate = urlParams.get('endDate') || urlParams.get('end');

    try {
      // Call API without employeeId to get all employees' data
      const response = await axios.get(`/api/v1/employee-ledger`, {
        params: {
          startDate,
          endDate
        }
      });

      if (response.data && response.data.success) {
        const data = response.data.data;
        setEmployees(data.employees || []);
        setAllLedgerEntries(data.ledgerEntries || []);
        setFilteredEntries(data.ledgerEntries || []);
        setSummary(data.summary);
      } else {
        toast.error('Failed to fetch employee ledger');
        setEmployees([]);
        setAllLedgerEntries([]);
        setFilteredEntries([]);
        setSummary(null);
      }
    } catch (error: any) {
      console.error('Error fetching employee ledger:', error);
      toast.error(error?.response?.data?.message || 'Failed to fetch employee ledger');
      setEmployees([]);
      setAllLedgerEntries([]);
      setFilteredEntries([]);
      setSummary(null);
    } finally {
      setIsLoading(false);
    }
  };

  const filterOnChangeHandler = (data: any) => {
    setFilters(data);
  };

  const filterdOnChangeHandler = (data: any) => {
    setFilterData(data);
  };

  const handleDownloadPdf = () => {
    toast.info('PDF download functionality will be implemented soon');
  };

  const handleDownloadExcel = () => {
    toast.info('Excel download functionality will be implemented soon');
  };

  // Handle scroll event
  const handleScroll = () => {
    if (!scrollContainerRef.current) return;

    const currentScrollTop = scrollContainerRef.current.scrollTop;
    const scrollHeight = scrollContainerRef.current.scrollHeight;
    const clientHeight = scrollContainerRef.current.clientHeight;

    // Determine scroll direction
    if (currentScrollTop > lastScrollTop.current) {
      setScrollDirection('down');
    } else if (currentScrollTop < lastScrollTop.current) {
      setScrollDirection('up');
    }

    lastScrollTop.current = currentScrollTop;

    // Show button when scrolling
    if (currentScrollTop > 100 || currentScrollTop < scrollHeight - clientHeight - 100) {
      setShowScrollButton(true);

      // Clear existing timeout
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }

      // Hide button after 2 seconds of no scrolling
      scrollTimeoutRef.current = setTimeout(() => {
        setShowScrollButton(false);
      }, 2000);
    }
  };

  // Handle scroll button click
  const handleScrollButtonClick = () => {
    if (!scrollContainerRef.current) return;

    const scrollHeight = scrollContainerRef.current.scrollHeight;

    if (scrollDirection === 'down') {
      // Scroll to bottom
      scrollContainerRef.current.scrollTo({
        top: scrollHeight,
        behavior: 'smooth'
      });
    } else {
      // Scroll to top
      scrollContainerRef.current.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Box sx={{ width: 'calc(100% - 7px)', height: 'calc(100vh - 70px)', display: 'flex', flexDirection: 'column', overflow: 'hidden', mb: 2, position: 'relative' }} className={'c-card2'}>
          {/* Scrollable content area */}
          <Box
            ref={scrollContainerRef}
            onScroll={handleScroll}
            sx={{
              flex: 1,
              overflow: 'auto',
              minHeight: 0,
              '&::-webkit-scrollbar': { display: 'none' },
              msOverflowStyle: 'none',
              scrollbarWidth: 'none'
            }}>
            {/* Report Header */}
            <ReusableReportHeader
              reportType="EMPLOYEE_LEDGER"
              title="Employee Ledger Report"
              subtitle={(filterData && filterData.periodString) ? filterData.periodString : 'Employee Ledger Statement'}
              recordCount={filteredEntries?.length || 0}
              isLoading={isLoading}
              onDownloadPDF={handleDownloadPdf}
              onDownloadExcel={handleDownloadExcel}
              showPrint={false}
              showShare={false}
            />

            {/* Filters and Search Section */}
            <Grid container px={2} py={1.5} alignItems="flex-start">
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
                  {/* Date Filter */}
                  <Box>
                    <Filters
                      type="report"
                      inline
                      filterHandler={filterOnChangeHandler}
                      selectedFilters={filterdOnChangeHandler}
                      hideFilter={true}
                      hidePartySearch={true}
                    />
                  </Box>

                  {/* Employee Selector - Multi-select */}
                  <Box sx={{ minWidth: '320px' }}>
                    <Autocomplete
                      multiple
                      size="small"
                      value={selectedEmployees}
                      onChange={(event, newValue) => {
                        setSelectedEmployees(newValue);
                      }}
                      options={employees}
                      getOptionLabel={(option) => `${option.name} (${option.empId})`}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          className='c-text-field-3'
                          variant="standard"
                          placeholder={selectedEmployees.length === 0 ? "Filter by Employee..." : ""}
                          InputProps={{
                            ...params.InputProps,
                            disableUnderline: true,
                          }}
                        />
                      )}
                      renderOption={(props, option) => (
                        <li {...props} key={option._id}>
                          <Box sx={{ width: '100%' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Typography variant="body2" fontWeight={500}>
                                {option.name}
                              </Typography>
                              <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                                ({option.empId})
                              </Typography>
                            </Box>
                            {option.designation && (
                              <Typography variant="caption" color="text.secondary">
                                {option.designation}
                              </Typography>
                            )}
                          </Box>
                        </li>
                      )}
                      isOptionEqualToValue={(option, value) => option._id === value._id}
                      noOptionsText="No employees found"
                    />
                  </Box>

                  {/* Search Field */}
                  <TextField
                    className='c-text-field-3'
                    variant="standard"
                    size="small"
                    placeholder="Search Employee, Voucher No., Ledger, Narration..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    sx={{ minWidth: '360px' }}
                    InputProps={{
                      disableUnderline: true,
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
              </Grid>
            </Grid>

            {!isLoading ? (
              <>
                {/* Summary Row Above Table */}
                {summary && (
                  <Box sx={{ px: 2, py: 1.5, backgroundColor: '#f8fafc', borderBottom: '2px solid #cbd5e1' }}>
                    <Grid container spacing={2}>
                      <Grid item xs={4}>
                        <Typography variant="body2" sx={{ fontSize: '0.875rem', fontWeight: 500, color: '#d32f2f' }}>
                          <strong>Total Debit:</strong> ₹ {Utils.formatNumber(summary.totalDebits.toFixed(2))}
                        </Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography variant="body2" sx={{ fontSize: '0.875rem', fontWeight: 500, color: '#2e7d32' }}>
                          <strong>Total Credit:</strong> ₹ {Utils.formatNumber(summary.totalCredits.toFixed(2))}
                        </Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography variant="body2" sx={{ fontSize: '0.875rem', fontWeight: 500, color: '#1976d2' }}>
                          <strong>Total Entries:</strong> {summary.totalEntries}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>
                )}

                {/* Transaction Table */}
                {filteredEntries.length > 0 && (
                  <Table sx={{ minWidth: 700, tableLayout: 'fixed', width: '100%' }} size='small'>
                    <colgroup>
                      <col style={{ width: '15%' }} />
                      <col style={{ width: '10%' }} />
                      <col style={{ width: '10%' }} />
                      <col style={{ width: '15%' }} />
                      <col style={{ width: '20%' }} />
                      <col style={{ width: '10%' }} />
                      <col style={{ width: '10%' }} />
                      <col style={{ width: '10%' }} />
                    </colgroup>
                    <TableHead>
                      <TableRow>
                        <StyledTableCell>Employee</StyledTableCell>
                        <StyledTableCell>Date</StyledTableCell>
                        <StyledTableCell>Voucher No.</StyledTableCell>
                        <StyledTableCell>Ledger Account</StyledTableCell>
                        <StyledTableCell>Narration</StyledTableCell>
                        <StyledTableCell style={{ textAlign: 'right' }}>Debit</StyledTableCell>
                        <StyledTableCell style={{ textAlign: 'right' }}>Credit</StyledTableCell>
                        <StyledTableCell style={{ textAlign: 'right' }}>Balance</StyledTableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredEntries.map((entry: LedgerEntry, index: number) => (
                        <StyledTableRow key={`${entry.journalEntryId}-${index}`}>
                          <StyledTableCell style={{ verticalAlign: 'middle' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', lineHeight: '1.3' }}>
                              <span style={{ fontWeight: '600', fontSize: '9pt' }}>
                                {entry.employeeName}
                              </span>
                              <span style={{ fontSize: '8pt', color: '#6b7280' }}>
                                {entry.employeeCode}
                              </span>
                            </div>
                          </StyledTableCell>
                          <StyledTableCell style={{ verticalAlign: 'middle' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', lineHeight: '1.5' }}>
                              <span style={{ fontWeight: '500', fontSize: '9pt' }}>
                                {moment(entry.date).format("DD MMM YYYY")}
                              </span>
                              <span style={{ fontSize: '8pt', color: '#6b7280', fontStyle: 'italic' }}>
                                {moment(entry.date).format("hh:mm A")}
                              </span>
                            </div>
                          </StyledTableCell>
                          <StyledTableCell style={{ fontSize: '9pt', fontWeight: '500', verticalAlign: 'middle' }}>
                            #{entry.voucherNumber}
                          </StyledTableCell>
                          <StyledTableCell style={{ verticalAlign: 'middle' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', lineHeight: '1.3' }}>
                              <span style={{ fontWeight: '500', fontSize: '9pt' }}>
                                {entry.ledgerName}
                              </span>
                              <span style={{ fontSize: '8pt', color: '#6b7280' }}>
                                {entry.primaryGroup}
                              </span>
                            </div>
                          </StyledTableCell>
                          <StyledTableCell style={{ verticalAlign: 'middle' }}>
                            <span style={{ fontSize: '8pt', fontStyle: 'italic', color: '#374151' }}>
                              {entry.narration}
                            </span>
                          </StyledTableCell>
                          <StyledTableCell style={{
                            textAlign: 'right',
                            fontWeight: entry.debit > 0 ? '600' : 'normal',
                            verticalAlign: 'middle'
                          }}>
                            {entry.debit > 0 ? '₹ ' + Utils.formatNumber(entry.debit.toFixed(2)) : ''}
                          </StyledTableCell>
                          <StyledTableCell style={{
                            textAlign: 'right',
                            fontWeight: entry.credit > 0 ? '600' : 'normal',
                            verticalAlign: 'middle'
                          }}>
                            {entry.credit > 0 ? '₹ ' + Utils.formatNumber(entry.credit.toFixed(2)) : ''}
                          </StyledTableCell>
                          <StyledTableCell style={{
                            textAlign: 'right',
                            fontWeight: 'bold',
                            fontSize: '9pt',
                            color: entry.balance >= 0 ? '#2e7d32' : '#d32f2f',
                            verticalAlign: 'middle'
                          }}>
                            ₹ {Utils.formatNumber(Math.abs(entry.balance).toFixed(2))} {entry.balance >= 0 ? 'Dr' : 'Cr'}
                          </StyledTableCell>
                        </StyledTableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}

                {/* Empty State */}
                {filteredEntries.length === 0 && !isLoading && (
                  <Box sx={{ textAlign: 'center', py: 8 }}>
                    <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                      No employee ledger entries found
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {selectedEmployees.length > 0 || searchTerm
                        ? 'Try adjusting your filters or search criteria'
                        : 'Select a date range to load employee ledger data'}
                    </Typography>
                  </Box>
                )}

                {/* Showing entries info */}
                {filteredEntries.length > 0 && (
                  <Box sx={{ p: 2, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <Typography variant="body2" color="textSecondary">
                      Showing {filteredEntries.length} of {allLedgerEntries.length} entries
                      {(selectedEmployees.length > 0 || searchTerm) && ' (filtered)'}
                    </Typography>
                  </Box>
                )}
              </>
            ) : (
              <Grid container p={2}>
                <Grid item xs={12} style={{ textAlign: 'center', padding: '60px 0' }}>
                  <CircularProgress color="info" />
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    Loading employee ledger data...
                  </Typography>
                </Grid>
              </Grid>
            )}
          </Box>

          {/* Fixed Total Row */}
          {!isLoading && summary && (
            <Box>
              <Table sx={{ minWidth: 700, tableLayout: 'fixed', width: '100%' }} size='small'>
                <colgroup>
                  <col style={{ width: '15%' }} />
                  <col style={{ width: '10%' }} />
                  <col style={{ width: '10%' }} />
                  <col style={{ width: '15%' }} />
                  <col style={{ width: '20%' }} />
                  <col style={{ width: '10%' }} />
                  <col style={{ width: '10%' }} />
                  <col style={{ width: '10%' }} />
                </colgroup>
                <TableBody>
                  <TableRow style={{ backgroundColor: '#f1f5f9' }}>
                    <StyledTableCell colSpan={5} style={{
                      fontWeight: 'bold',
                      fontSize: '11pt',
                      backgroundColor: '#f1f5f9',
                      color: '#1e293b',
                      border: '0.75px solid #d9d4d4',
                      borderTop: '2px solid #64748b',
                      padding: 5,
                      paddingLeft: '15px !important'
                    }}>
                      TOTAL
                    </StyledTableCell>
                    <StyledTableCell style={{
                      textAlign: 'right',
                      fontWeight: 'bold',
                      fontSize: '10pt',
                      color: '#dc2626',
                      backgroundColor: '#fee2e2',
                      border: '0.75px solid #d9d4d4',
                      borderTop: '2px solid #64748b',
                      borderLeft: '1px solid #dc2626',
                      borderRight: '1px solid #dc2626',
                      borderBottom: '1px solid #dc2626',
                      padding: 5,
                      paddingLeft: '15px !important',
                      paddingRight: '15px !important'
                    }}>
                      ₹ {Utils.formatNumber(summary.totalDebits.toFixed(2))}
                    </StyledTableCell>
                    <StyledTableCell style={{
                      textAlign: 'right',
                      fontWeight: 'bold',
                      fontSize: '10pt',
                      color: '#059669',
                      backgroundColor: '#dcfce7',
                      border: '0.75px solid #d9d4d4',
                      borderTop: '2px solid #64748b',
                      borderLeft: '1px solid #059669',
                      borderRight: '1px solid #059669',
                      borderBottom: '1px solid #059669',
                      padding: 5,
                      paddingLeft: '15px !important',
                      paddingRight: '15px !important'
                    }}>
                      ₹ {Utils.formatNumber(summary.totalCredits.toFixed(2))}
                    </StyledTableCell>
                    <StyledTableCell style={{
                      backgroundColor: '#f1f5f9',
                      border: '0.75px solid #d9d4d4',
                      borderTop: '2px solid #64748b',
                      padding: 5
                    }}>
                    </StyledTableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Box>
          )}

          {/* Scroll to Top/Bottom Button */}
          <Fade in={showScrollButton}>
            <Fab
              color="primary"
              size="medium"
              onClick={handleScrollButtonClick}
              sx={{
                position: 'absolute',
                bottom: 46,
                right: 16,
                zIndex: 1000,
                boxShadow: 3,
                '&:hover': {
                  boxShadow: 6,
                  transform: 'scale(1.05)',
                },
                transition: 'all 0.3s ease-in-out'
              }}
              aria-label={scrollDirection === 'down' ? 'scroll to bottom' : 'scroll to top'}
            >
              {scrollDirection === 'down' ? <ArrowDownIcon /> : <ArrowUpIcon />}
            </Fab>
          </Fade>
        </Box>
      </Grid>
    </Grid>
  );
};

export default EmployeeLedger;
